<template>
  <div
    class="project-carousel"
    @mouseenter="isPaused = true"
    @mouseleave="isPaused = false"
    @focusin="isPaused = true"
    @focusout="isPaused = false"
  >
    <van-swipe
      ref="swipeRef"
      class="project-carousel__swipe"
      :duration="isResetting ? 0 : TRANSITION_DURATION"
      :initial-swipe="slides.length > 1 ? 1 : 0"
      :loop="false"
      :show-indicators="false"
      :stop-propagation="false"
      @change="handleTrackChange"
      @drag-start="handleDragStart"
      @drag-end="handleDragEnd"
    >
      <van-swipe-item
        v-for="(slide, index) in renderedSlides"
        :key="`${slide}-${index}`"
      >
        <div class="project-carousel__image-wrap">
          <img
            class="project-carousel__image"
            :src="slide"
            :alt="`${altPrefix} - ${imageLabel} ${toLogicalIndex(index) + 1}`"
            draggable="false"
          />
        </div>
      </van-swipe-item>
    </van-swipe>

    <div class="project-carousel__dots" :aria-label="dotsLabel">
      <button
        v-for="(_, index) in slides"
        :key="index"
        type="button"
        class="project-carousel__dot"
        :class="{ 'is-active': currentIndex === index }"
        :aria-label="`${switchLabel} ${index + 1}`"
        :aria-current="currentIndex === index ? 'true' : undefined"
        @click="goTo(index)"
      ></button>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  defineProps,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  slides: string[];
  altPrefix: string;
}>();

const AUTOPLAY_INTERVAL = 3500;
const TRANSITION_DURATION = 500;
const RESET_SETTLE_DELAY = 100;

const { locale } = useI18n();
interface SwipeInstance {
  swipeTo: (index: number) => void;
}

const swipeRef = ref<SwipeInstance | null>(null);
const currentIndex = ref(0);
const isPaused = ref(false);
const isResetting = ref(false);
const isDragging = ref(false);
let autoplayTimer: number | undefined;
let resetTimer: number | undefined;

const renderedSlides = computed(() => {
  if (props.slides.length <= 1) return props.slides;

  return [
    props.slides[props.slides.length - 1],
    ...props.slides,
    props.slides[0],
  ];
});

const dotsLabel = computed(() =>
  locale.value === "zh" ? "图片切换控制器" : "Image carousel controls"
);
const switchLabel = computed(() =>
  locale.value === "zh" ? "切换到图片" : "Go to image"
);
const imageLabel = computed(() =>
  locale.value === "zh" ? "图片" : "image"
);

const toLogicalIndex = (trackIndex: number) => {
  const count = props.slides.length;
  if (count <= 1) return 0;
  return (trackIndex - 1 + count) % count;
};

const clearAutoplay = () => {
  if (autoplayTimer !== undefined) {
    window.clearTimeout(autoplayTimer);
    autoplayTimer = undefined;
  }
};

const clearReset = () => {
  if (resetTimer !== undefined) {
    window.clearTimeout(resetTimer);
    resetTimer = undefined;
  }
};

const scheduleAutoplay = () => {
  clearAutoplay();
  if (
    isPaused.value ||
    isDragging.value ||
    isResetting.value ||
    resetTimer !== undefined ||
    props.slides.length <= 1
  ) {
    return;
  }

  autoplayTimer = window.setTimeout(() => {
    autoplayTimer = undefined;
    swipeRef.value?.swipeTo(currentIndex.value + 2);
  }, AUTOPLAY_INTERVAL);
};

const resetFromClone = (trackIndex: number) => {
  clearAutoplay();
  clearReset();

  resetTimer = window.setTimeout(async () => {
    resetTimer = undefined;
    isResetting.value = true;
    await nextTick();

    const targetIndex = trackIndex === 0 ? props.slides.length : 1;
    swipeRef.value?.swipeTo(targetIndex);

    resetTimer = window.setTimeout(() => {
      resetTimer = undefined;
      isResetting.value = false;
      scheduleAutoplay();
    }, RESET_SETTLE_DELAY);
  }, TRANSITION_DURATION);
};

const handleTrackChange = (trackIndex: number) => {
  currentIndex.value = toLogicalIndex(trackIndex);

  const cloneAtStart = trackIndex === 0;
  const cloneAtEnd = trackIndex === props.slides.length + 1;
  if (props.slides.length > 1 && (cloneAtStart || cloneAtEnd)) {
    resetFromClone(trackIndex);
    return;
  }

  scheduleAutoplay();
};

const handleDragStart = () => {
  isDragging.value = true;
  clearAutoplay();
};

const handleDragEnd = () => {
  isDragging.value = false;
  scheduleAutoplay();
};

const goTo = (index: number) => {
  clearReset();
  isResetting.value = false;
  swipeRef.value?.swipeTo(index + 1);
};

watch(isPaused, (paused) => {
  if (paused) clearAutoplay();
  else scheduleAutoplay();
});

onMounted(scheduleAutoplay);
onBeforeUnmount(() => {
  clearAutoplay();
  clearReset();
});
</script>

<style lang="less" scoped>
.project-carousel {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 15px;
  background: #ffffff;
  box-shadow: 0 0 9px 1px rgba(0, 0, 0, 0.035);
}

.project-carousel__swipe,
.project-carousel__image-wrap {
  width: 100%;
  height: 100%;
}

.project-carousel__swipe {
  overflow: hidden;
  cursor: grab;
  touch-action: pan-y pinch-zoom;

  &:active { cursor: grabbing; }
}

.project-carousel__image-wrap { overflow: hidden; }

.project-carousel__image {
  display: block;
  width: 100%;
  height: 100%;
  padding: 18px 18px 42px;
  object-fit: contain;
  user-select: none;
}

.project-carousel__dots {
  position: absolute;
  z-index: 2;
  right: 24px;
  bottom: 15px;
  left: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.project-carousel__dot {
  width: 6px;
  height: 6px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #d1d5dc;
  cursor: pointer;
  transition: background-color 0.25s ease, transform 0.25s ease;

  &:hover { background: #7ba8ff; }
  &.is-active { background: #0757ff; transform: scale(1.35); }

  &:focus-visible {
    outline: 2px solid rgba(7, 87, 255, 0.35);
    outline-offset: 3px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .project-carousel__dot { transition: none; }
}
</style>
